import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer, ClipboardList, FileText, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/constants";

function printHtml(title, bodyHtml) {
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${title}</title><style>
    body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;margin:28px;font-size:13px}
    h1{font-size:20px;margin:0 0 2px} h2{font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#0e7490;margin:20px 0 6px;border-bottom:2px solid #0e7490;padding-bottom:3px}
    table{width:100%;border-collapse:collapse;margin-top:4px} th,td{border:1px solid #cbd5e1;padding:5px 8px;text-align:left;font-size:12px}
    th{background:#f1f5f9;text-transform:uppercase;font-size:10px;letter-spacing:.5px}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin-top:8px}
    .meta div{padding:3px 0;border-bottom:1px dotted #e2e8f0}
    .lbl{color:#64748b;font-size:10px;text-transform:uppercase;letter-spacing:.5px}
    .jn{font-family:monospace;color:#0e7490;font-weight:bold;font-size:15px}
    .photos{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-top:6px}
    .photos img{width:100%;max-height:260px;object-fit:cover;border:1px solid #cbd5e1}
    @media print {.noprint{display:none}}
  </style></head><body>${bodyHtml}
  <script>window.onload=()=>setTimeout(()=>window.print(),300)</scr` + `ipt></body></html>`);
  w.document.close();
}

function bomTable(groups) {
  if (!groups?.length) return "<p style='color:#64748b'>No quote linked — no bill of materials available.</p>";
  return groups.map((g) => `
    <h2>${g.section}</h2>
    <table><thead><tr><th style="width:50%">Item</th><th>Qty</th><th>Unit</th><th>Notes</th></tr></thead>
    <tbody>${g.items.map((i) => `<tr><td>${i.item}</td><td>${i.qty}</td><td>${i.unit}</td><td>${i.notes || ""}</td></tr>`).join("")}</tbody></table>`).join("");
}

export function BomDialog({ jobId, open, onClose }) {
  const { data: bom, isLoading } = useQuery({
    queryKey: ["job-bom", jobId], enabled: open,
    queryFn: async () => (await api.get(`/jobs/${jobId}/bom`)).data,
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="job-bom-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">
          Bill of Materials {bom?.job_number && <span className="font-mono text-sm text-cyan-700 ml-2">{bom.job_number}</span>}
        </DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
          {isLoading && <div className="py-8 text-center"><Loader2 size={18} className="animate-spin inline text-slate-400" /></div>}
          {bom && !bom.has_quote && <p className="text-sm text-amber-600" data-testid="bom-no-quote">No quote linked to this job — the BOM is generated from the accepted quote.</p>}
          {(bom?.groups || []).map((g, gi) => (
            <div key={gi} data-testid={`bom-group-${gi}`}>
              <div className="stamp-label text-cyan-700 mb-1">{g.section}</div>
              <table className="w-full text-xs border border-slate-200">
                <thead><tr className="bg-slate-50 text-left stamp-label text-slate-500">
                  <th className="px-2 py-1.5 w-1/2">Item</th><th className="px-2 py-1.5">Qty</th><th className="px-2 py-1.5">Unit</th><th className="px-2 py-1.5">Notes</th>
                </tr></thead>
                <tbody>
                  {g.items.map((i, ii) => (
                    <tr key={ii} className="border-t border-slate-100">
                      <td className="px-2 py-1.5">{i.item}</td>
                      <td className="px-2 py-1.5 font-mono">{i.qty}</td>
                      <td className="px-2 py-1.5 text-slate-500">{i.unit}</td>
                      <td className="px-2 py-1.5 text-slate-400">{i.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
          <Button onClick={() => printHtml(`BOM ${bom?.job_number || ""}`,
            `<h1>Bill of Materials</h1><div class="jn">${bom?.job_number || ""}</div><div>${bom?.title || ""}${bom?.quote_number ? " · from " + bom.quote_number : ""}</div>${bomTable(bom?.groups)}`)}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="bom-print-btn">
            <Printer size={14} className="mr-1.5" /> Print BOM
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function handoverPrint(h) {
  const photos = (h.site_photos || []).map((p) => `<div><img src="${p.url}"/>${p.caption ? `<div class="lbl">${p.caption}</div>` : ""}</div>`).join("");
  printHtml(`Handover ${h.job_number || ""}`, `
    <h1>Installer Handover Sheet</h1>
    <div class="jn">${h.job_number || "—"}</div>
    <div class="meta">
      <div><span class="lbl">Client</span><br/><b>${h.client_name || "—"}</b></div>
      <div><span class="lbl">Contact</span><br/><b>${h.contact_name || "—"}</b> ${h.contact_phone ? "· " + h.contact_phone : ""}</div>
      <div><span class="lbl">Site address</span><br/><b>${h.site_address || "—"}</b></div>
      <div><span class="lbl">Scheduled start</span><br/><b>${h.scheduled_start ? fmtDate(h.scheduled_start) : "TBC"}</b></div>
      <div><span class="lbl">Quote ref</span><br/>${h.quote_number || "—"}</div>
      <div><span class="lbl">Crew</span><br/>${(h.crew || []).join(", ") || "—"}</div>
    </div>
    <h2>Description of works</h2>
    <p style="white-space:pre-wrap">${h.scope_of_works || "See quote."}</p>
    <h2>Bill of materials</h2>${bomTable(h.bom)}
    ${photos ? `<h2>Site photos</h2><div class="photos">${photos}</div>` : ""}
  `);
}

export function JobDocButtons({ jobId }) {
  const [bomOpen, setBomOpen] = useState(false);
  const [loadingHandover, setLoadingHandover] = useState(false);
  const doHandover = async () => {
    setLoadingHandover(true);
    try {
      const h = (await api.get(`/jobs/${jobId}/handover`)).data;
      handoverPrint(h);
    } finally {
      setLoadingHandover(false);
    }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setBomOpen(true)} className="rounded-sm h-10" data-testid="job-bom-btn">
        <ClipboardList size={15} className="mr-1.5" /> Bill of Materials
      </Button>
      <Button data-testid="print-handover-btn" onClick={doHandover} disabled={loadingHandover}
        className="rounded-sm h-10 bg-slate-900 hover:bg-slate-800 text-white">
        {loadingHandover ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Printer size={16} className="mr-2" />} Handover sheet
      </Button>
      <BomDialog jobId={jobId} open={bomOpen} onClose={() => setBomOpen(false)} />
    </>
  );
}
